import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { createApiContext } from "../../../shared/api/create-api-context";
import type { PlanPayload } from "../model/plans";
import type { Training, TrainingApi } from "./training-api";

const context = createApiContext<TrainingApi>("Training");
export const TrainingApiProvider = context.Provider;
export const useTrainingApi = context.useApi;

export const trainingKeys = {
  all: (profileId: string) => ["training", profileId] as const,
  plans: (profileId: string, page: number) =>
    [...trainingKeys.all(profileId), "plans", page] as const,
};

export function useTrainingPlans(profileId: string, page = 0): UseQueryResult<Training[]> {
  const api = useTrainingApi();
  return useQuery({
    queryKey: trainingKeys.plans(profileId, page),
    queryFn: ({ signal }) => api.plans(profileId, page, signal),
    enabled: profileId !== "",
  });
}

export function useSaveTrainingPlan(profileId: string) {
  const api = useTrainingApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { payload: PlanPayload; baseId: string | null }) =>
      api.savePlan(profileId, input.payload, input.baseId),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: trainingKeys.all(profileId) });
    },
  });
}

export function useActivateTrainingPlan(profileId: string) {
  const api = useTrainingApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => api.activatePlan(planId),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: trainingKeys.all(profileId) });
    },
  });
}

export function useDeleteTrainingPlan(profileId: string) {
  const api = useTrainingApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => api.deletePlan(planId),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: trainingKeys.all(profileId) });
    },
  });
}
